import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';

import { Card } from 'components/Card';
import Message from 'components/Message';
import Star from 'components/Svgs/Star';
import ReviewsStatisticsEmpty from './ReviewsStatisticsEmpty';
import ReviewsStatisticsHeader from './ReviewsStatisticsHeaderContainer';
import './ReviewsStatistics.scss';

const messages = defineMessages({
  last90days: {
    id: 'reviews-statistics.last-90-days',
    defaultMessage: 'Last 90 days',
  },
  overallRating: {
    id: 'reviews-statistics.overall-rating',
    defaultMessage: 'Overall rating',
  },
  numberOfRatings: {
    id: 'reviews-statistics.number-of-ratings',
    defaultMessage: '{ratings, plural, one {# rating} other {# ratings}}',
  },
});

const ReviewsStatistics = ({
  reviewsStatistics: { averageRatings, overallRatings, isEmpty },
  isLoading,
  hasActiveFilters,
}) => {
  if (isLoading || isEmpty) return <ReviewsStatisticsEmpty isLoading={isLoading} hasActiveFilters={hasActiveFilters} />;

  if (!averageRatings) return null;

  const { overTimeWindow, overall: overallAvarageRatings } = averageRatings;

  const ratingValues = [5, 4, 3, 2, 1];
  const sumOverallRatings = overallRatings.reduce((acc, element) => acc + element.count, 0);
  const chartData = ratingValues.map(
    ratingValue => overallRatings.find(({ value }) => value === ratingValue) || { value: ratingValue, count: 0 }
  );
  const overTimeRatingsDifference = overTimeWindow.value.toFixed(1) * 10 - overallAvarageRatings.value.toFixed(1) * 10;

  const showDifferenceArrow = overTimeRatingsDifference !== 0 && overTimeWindow.value !== 0;

  const differenceArrow =
    overTimeRatingsDifference < 0 ? (
      <span className="reviews-statistics__rating-triangle-down" />
    ) : (
      <span className="reviews-statistics__rating-triangle-up" />
    );

  return (
    <Card flush>
      <div className="reviews-statistics grid grid--flush">
        <div className="grid__item 1/1">
          <ReviewsStatisticsHeader />
        </div>
        <div className="grid__item lg-1/4 md-1/1 sm-1/1 reviews-statistics__cell">
          <Message as="p" className="reviews-statistics__title" message={messages.overallRating} />
          <p className="reviews-statistics__value" data-cypress="overal-rating-avarage">
            <Message format="one-decimal" value={overallAvarageRatings.value} />
            <Star className="reviews-statistics__star" />
          </p>
          <Message
            as="p"
            className="reviews-statistics__count"
            values={{ ratings: overallAvarageRatings.count }}
            data-cypress="overal-rating-count"
            message={messages.numberOfRatings}
          />
        </div>
        <div className="grid__item lg-1/4 md-1/1 sm-1/1 reviews-statistics__cell">
          <Message as="p" className="reviews-statistics__title" message={messages.last90days} />
          <p className="reviews-statistics__value" data-cypress="last90days-rating-avarage">
            <Message format="one-decimal" value={overTimeWindow.value} />
            <Star className="reviews-statistics__star" />
            {showDifferenceArrow && differenceArrow}
          </p>
          <Message
            as="p"
            className="reviews-statistics__count"
            values={{ ratings: overTimeWindow.count }}
            data-cypress="last90days-rating-count"
            message={messages.numberOfRatings}
          />
        </div>
        <div className="grid__item lg-1/2 md-1/1 sm-1/1">
          <div className="reviews-statistics__chart">
            {chartData.map(({ value, count }) => (
              <p key={value} data-cypress={`reviews-statistics-chart-value-${value}`}>
                <span className="reviews-statistics__number">{value}</span>
                <Star className="reviews-statistics__star" />
                <progress max={sumOverallRatings} value={count} />
                <Message value={count} />
              </p>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
};

const statisticShape = PropTypes.shape({
  value: PropTypes.number,
  count: PropTypes.number,
});

ReviewsStatistics.propTypes = {
  reviewsStatistics: PropTypes.shape({
    averageRatings: PropTypes.shape({
      overTimeWindow: statisticShape,
      overall: statisticShape,
    }),
    overallAvarageRatings: statisticShape,
    overallRatings: PropTypes.arrayOf(statisticShape),
    isEmpty: PropTypes.bool,
  }),
  isLoading: PropTypes.bool,
  hasActiveFilters: PropTypes.bool,
};

export default ReviewsStatistics;
